import { BatchClassConfig } from "../../models/config/BatchClassConfig";
import { DocTypeConfigData } from "../../models/config/DocTypeConfig";
import { TableConfigData } from "../../models/config/TableConfig";

export { removeDuplicatesOrEmpty, cleanBatchClassConfig };
/**
 * Remove entries from array where the result of key() is the n+1th result, or key() is ""
 * @param stuff
 * @param key
 * @returns
 */
function removeDuplicatesOrEmpty<T>(stuff: T[], key: (x: T) => string): T[] {
  const keys = stuff.map(key);
  return stuff.filter(
    (x, i, arr) => keys.indexOf(key(x)) === i && keys[i].trim() !== "",
  );
}

/**
 *
 * @param batchclassconfig_ batch class config to check and cleanup
 * @returns Tuple of cleaned up BatchClassConfig and boolean true if the batch class was cleaned
 */
function cleanBatchClassConfig(
  batchclassconfig_: BatchClassConfig,
): [BatchClassConfig, boolean, string] {
  let cleaned = false;
  const batchclassconfig = batchclassconfig_.toData();
  let invalidDocTypes: DocTypeConfigData[] = [];
  const invalidTableConfigs: TableConfigData[] = [];

  let newDocTypes = removeDuplicatesOrEmpty(
    batchclassconfig.docTypes,
    (x) => x.typeName,
  );
  newDocTypes = removeDuplicatesOrEmpty(newDocTypes, (x) => x.description);
  if (newDocTypes.length !== batchclassconfig.docTypes.length) {
    console.debug(
      `After: BatchClassConfig ${batchclassconfig.batchClassId} has ${newDocTypes.length} DocTypes from ${batchclassconfig.docTypes.length}`,
    );
    // get DocTypes that were filtered out
    invalidDocTypes = batchclassconfig.docTypes.filter(
      (x) => !newDocTypes.includes(x),
    );
    // remove bad doc types
    batchclassconfig.docTypes = newDocTypes;
    cleaned = true;
  }

  for (const doctype of batchclassconfig.docTypes) {
    let newFields = removeDuplicatesOrEmpty(doctype.fields, (x) => x.fieldName);
    newFields = removeDuplicatesOrEmpty(newFields, (x) => x.description);
    if (newFields.length !== doctype.fields.length) {
      console.debug(
        `After: Doctype ${doctype.typeName} has ${newFields.length} DLFs from ${doctype.fields.length}`,
      );
      invalidDocTypes.push(doctype);
      // remove bad fields
      doctype.fields = newFields;
      cleaned = true;
    }

    let newTables = removeDuplicatesOrEmpty(doctype.tables, (x) => x.name);
    if (batchclassconfig.helios) {
      newTables = removeDuplicatesOrEmpty(
        newTables,
        (x) => x.helios?.description ?? "",
      );
    }
    if (newTables.length !== doctype.tables.length) {
      console.debug(
        `After: Doctype ${doctype.typeName} has ${newTables.length} Tables from ${doctype.tables.length}`,
      );
      // get DocTypeTables that were filtered out
      invalidTableConfigs.push(
        ...doctype.tables.filter((x) => !newTables.includes(x)),
      );
      invalidDocTypes.push(doctype);
      // remove bad tables
      doctype.tables = newTables;
      cleaned = true;
    }

    for (const table of doctype.tables) {
      let newCols = removeDuplicatesOrEmpty(
        table.columnNames,
        (x) => x.fieldName,
      );
      newCols = removeDuplicatesOrEmpty(newCols, (x) => x.description);
      if (newCols.length !== table.columnNames.length) {
        console.debug(
          `After: Table ${table.name} has ${newCols.length} Columns from ${table.columnNames.length}`,
        );
        invalidDocTypes.push(doctype);
        invalidTableConfigs.push(table);
        table.columnNames = newCols;
        cleaned = true;
      }
    }
  }

  /*  */
  let errorMessage = "";

  // iterate unique doc types with errors add to message.
  for (const dtc of invalidDocTypes.filter(
    (x, i, arr) => arr.indexOf(x) === i,
  )) {
    let dtcNameMsg = `"${dtc.typeName}"`;
    if (dtc.description !== "") {
      dtcNameMsg += ` (${dtc.description})`;
    }
    errorMessage += `The Document Type ${dtcNameMsg} was not properly configured\n`;
  }

  // iterate unique table configss with errors add to message.
  for (const tableConf of invalidTableConfigs.filter(
    (x, i, arr) => arr.indexOf(x) === i,
  )) {
    let tableNameMsg = `"${tableConf.name}"`;
    if (tableConf.helios && tableConf.helios.description.length > 0) {
      tableNameMsg += ` (${tableConf.helios.description})`;
    }
    errorMessage += `The Table ${tableNameMsg} was not properly configured\n`;
  }

  return [new BatchClassConfig(batchclassconfig), cleaned, errorMessage];
}
